/**
 * ExpenseStatisticsCard - 费用统计卡片组件
 * 
 * 显示费用统计数据的卡片，包括总支出、已批准数、待审核数和人均支出
 */
import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Pressable,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { MODULE_COLORS } from '@cubemeet/core';

import type { MemberBalance } from './MemberBalanceList';
import type { PrivateSettlement } from './PrivateSettlementList';

export interface ExpenseStatistics {
  /**
   * 已批准总金额
   */
  totalApproved: number;
  /**
   * 待审核总金额
   */
  totalPending: number;
  /**
   * 已批准收据数量
   */
  approvedCount: number;
  /**
   * 待审核收据数量
   */
  pendingCount: number;
  /**
   * 人均/份额金额
   */
  averagePerPerson: number;
  /**
   * 成员余额列表（用于在成员余额列表中渲染）
   */
  memberBalances?: MemberBalance[];
  /**
   * 待结算/分账项列表
   */
  settlements?: PrivateSettlement[];
}

export interface PaymentModeInfo {
  /**
   * 支付模式：prepaid=预付, actual_expense=实际支出
   */
  mode: 'prepaid' | 'actual_expense';
  /**
   * 是否使用份额分账
   */
  useShares: boolean;
}

export interface ExpenseStatisticsCardProps {
  /**
   * 统计数据
   */
  statistics: ExpenseStatistics;
  /**
   * 货币代码
   */
  currency?: string;
  /**
   * 支付模式信息
   */
  paymentMode: PaymentModeInfo;
  /**
   * 是否为私账模式
   */
  isPrivateMode?: boolean;
  /**
   * 是否为创建者（控制设置按钮显示）
   */
  isCreator?: boolean;
  /**
   * 格式化货币显示（紧凑格式）
   */
  formatCompact: (amount: number, currency?: string) => string;
  /**
   * 格式化货币显示（完整格式）
   */
  formatFull: (amount: number, currency?: string) => string;
  /**
   * 当金额超过此阈值时使用紧凑显示并在点击时展示完整金额模态（默认 10000）
   */
  compactThreshold?: number;
  /**
   * 点击金额查看完整数值的回调
   */
  onAmountPress?: (fullAmount: string) => void;
  /**
   * 打开设置的回调
   */
  onOpenSettings?: () => void;
  /**
   * 翻译函数
   */
  t?: (key: string, params?: Record<string, any>) => string;
}

const defaultT = (key: string) => {
  const translations: Record<string, string> = {
    'statistics.title': '费用统计',
    'statistics.settings': '设置',
    'statistics.consumed': '已消费',
    'statistics.totalExpense': '总支出',
    'statistics.approvedReceipts': '已批准',
    'statistics.pendingReview': '待审核',
    'statistics.averagePerPerson': '人均',
    'statistics.averagePerShare': '份额平均',
    'paymentMode.prepaid': '预付模式',
    'paymentMode.actualExpense': '实际支出模式',
    'paymentMode.shareMode': '份额分账',
    'paymentMode.equalMode': '等额分账',
    'privateMode.info': '这是成员个人记录的私账，仅影响个人分账。',
    'statistics.close': '关闭',
  };
  return translations[key] || key;
};

/**
 * ExpenseStatisticsCard 组件
 */
const ExpenseStatisticsCard: React.FC<ExpenseStatisticsCardProps> = ({
  statistics,
  currency,
  paymentMode,
  isPrivateMode = false,
  isCreator = false,
  formatCompact,
  formatFull,
  onAmountPress,
  onOpenSettings,
  t = defaultT,
  compactThreshold = 10000,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [modalAmount, setModalAmount] = useState<number | null>(null);

  const handleAmountPress = (amount: number) => {
    // If consumer provided a handler, defer to it (legacy behavior)
    if (onAmountPress) {
      onAmountPress(formatFull(amount, currency));
      return;
    }

    // 自动决定是否展示模态：当金额绝对值超过阈值时展示完整金额
    if (Math.abs(amount) >= compactThreshold) {
      setModalAmount(amount);
      setModalVisible(true);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setModalAmount(null);
  };

  const modalFullLabel = useMemo(() => {
    if (modalAmount == null) return '';
    return formatFull(modalAmount, currency);
  }, [modalAmount, formatFull, currency]);

  return (
    <>
      <View style={styles.card}>
      {/* 标题和设置按钮 */}
      <View style={styles.header}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={{ marginRight: 8 }}>
            <Icon name="account-balance" size={18} color={MODULE_COLORS.travel} />
          </View>
          <Text style={styles.title}>{t('statistics.title')}</Text>
        </View>
        {isCreator && onOpenSettings && !isPrivateMode && (
          <TouchableOpacity style={styles.settingsButton} onPress={onOpenSettings}>
            <Icon name="tune" size={16} color={MODULE_COLORS.travel} />
            <Text style={styles.settingsText}>{t('statistics.settings')}</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* 统计数据网格 */}
      <View style={styles.statsGrid}>
        {/* 总支出 */}
        <View style={styles.statItem}>
          <View style={styles.statIcon}>
            <Icon name="account-balance-wallet" size={18} color={MODULE_COLORS.travel} />
          </View>
          <TouchableOpacity
            onPress={() => handleAmountPress(statistics.totalApproved)}
            activeOpacity={0.8}
          >
            <Text
              style={styles.statValue}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.8}
              ellipsizeMode="tail"
            >
              {formatCompact(statistics.totalApproved, currency)}
            </Text>
          </TouchableOpacity>
          <Text style={styles.statLabel}>
            {paymentMode.mode === 'prepaid'
              ? t('statistics.consumed')
              : t('statistics.totalExpense')}
          </Text>
        </View>

        {/* 已批准数 */}
        <View style={styles.statItem}>
          <View style={styles.statIcon}>
            <Icon name="receipt" size={18} color={MODULE_COLORS.travel} />
          </View>
          <Text style={styles.statValue}>{statistics.approvedCount}</Text>
          <Text style={styles.statLabel}>{t('statistics.approvedReceipts')}</Text>
        </View>

        {/* 待审核数 */}
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#fef3c7' }]}>
            <Icon name="more-horiz" size={18} color="#f59e0b" />
          </View>
          <Text style={styles.statValue}>{statistics.pendingCount}</Text>
          <Text style={styles.statLabel}>{t('statistics.pendingReview')}</Text>
        </View>

        {/* 人均/份额 */}
        <View style={styles.statItem}>
          <View style={styles.statIcon}>
            <Icon name="people" size={18} color={MODULE_COLORS.travel} />
          </View>
          <TouchableOpacity
            onPress={() => handleAmountPress(statistics.averagePerPerson)}
            activeOpacity={0.8}
          >
            <Text
              style={styles.statValue}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.8}
              ellipsizeMode="tail"
            >
              {formatCompact(statistics.averagePerPerson, currency)}
            </Text>
          </TouchableOpacity>
          <Text style={styles.statLabel}>
            {paymentMode.useShares
              ? t('statistics.averagePerShare')
              : t('statistics.averagePerPerson')}
          </Text>
        </View>
      </View>

      {/* 分账模式说明 */}
      <View style={styles.modeIndicator}>
        <View style={styles.modeInfo}>
          <Icon
            name={isPrivateMode ? 'lock' : (paymentMode.mode === 'prepaid' ? 'credit-card' : 'receipt')}
            size={16}
            color={MODULE_COLORS.travel}
          />
          <Text style={styles.modeText}>
            {isPrivateMode ? (
              t('privateMode.info')
            ) : (
              <>
                {paymentMode.mode === 'prepaid'
                  ? t('paymentMode.prepaid')
                  : t('paymentMode.actualExpense')}
                {' • '}
                {paymentMode.useShares
                  ? t('paymentMode.shareMode')
                  : t('paymentMode.equalMode')}
              </>
            )}
          </Text>
        </View>
      </View>
      </View>

      {/* 内置模态：在未提供 onAmountPress 时，点击紧凑金额会弹出完整金额 */}
      <Modal
        visible={modalVisible}
        animationType="fade"
        transparent
        onRequestClose={closeModal}
      >
        <Pressable style={styles.modalOverlay} onPress={closeModal}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('statistics.totalExpense')}</Text>
            <Text style={styles.modalAmount}>{modalFullLabel}</Text>
            <TouchableOpacity style={styles.modalClose} onPress={closeModal}>
              <Text style={styles.modalCloseText}>{t('statistics.close')}</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    // react-native-web deprecates shadow* props — use boxShadow on web
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  settingsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#f0f9ff',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: MODULE_COLORS.travel,
    gap: 4,
  },
  settingsText: {
    fontSize: 12,
    color: MODULE_COLORS.travel,
    fontWeight: '500',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  statItem: {
    width: '50%',
    paddingHorizontal: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  statIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#f0f9ff',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
  modeIndicator: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f1f5f9',
  },
  modeInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  modeText: {
    flex: 1,
    fontSize: 13,
    color: '#64748b',
    lineHeight: 18,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  modalAmount: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 16,
  },
  modalClose: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
  },
  modalCloseText: {
    fontSize: 14,
    color: MODULE_COLORS.travel,
    fontWeight: '600',
  },
});

export default ExpenseStatisticsCard;
